// Matching appuntamento (calendario iCloud del videomaker) ↔ cliente.
// Usato per capire se un cliente ha già le riprese fissate in agenda e quindi
// sopprimere l'alert "Occorrono subito delle riprese".
// Condiviso tra Dashboard e Videomaker per garantire logica identica.

import { Contenuto } from '../types';
import { isPost } from './tipoContenuto';

// ─────────────────────────────────────────────────────────────────────────────
// Logica RIPRESE condivisa (Dashboard + Videomaker) — UNICA fonte di verità.
// NON duplicare questi conteggi nelle pagine: importarli da qui.
// ─────────────────────────────────────────────────────────────────────────────

// Reel EFFETTIVAMENTE girati di un cliente nel mese = quelli che il videomaker
// ha confermato a mano come registrati (videoRegistrato=true) CON data di
// registrazione (videoRegistratoIl) nel mese, qualunque sia lo stato oggi: da
// programmare, programmati, pubblicati o archiviati. Lo stato "programmato" NON
// basta: il calendario editoriale viene spesso riempito con la sola IDEA del
// contenuto mentre il video non è ancora stato girato. ADV inclusi.
// Stessa regola della scheda "Registrati" nella pagina del cliente
// (ClientCalendar.tsx): prima qui contava il mese di pubblicazione ed erano
// esclusi gli ADV, e i due numeri non tornavano (Aline, 29/10/2026).
export const reelGiratiNelMese = (
  contenutiCliente: Contenuto[],
  meseCorrente: number,
  annoCorrente: number,
): Contenuto[] =>
  contenutiCliente.filter((c) => {
    if (!c.videoRegistrato || isPost(c) || !c.videoRegistratoIl) return false;
    const d = new Date(c.videoRegistratoIl);
    return d.getMonth() === meseCorrente && d.getFullYear() === annoCorrente;
  });

export const reelGiaGiratiCliente = (
  contenutiCliente: Contenuto[],
  meseCorrente: number,
  annoCorrente: number,
): number => reelGiratiNelMese(contenutiCliente, meseCorrente, annoCorrente).length;

export interface StatoRiprese {
  reelGiaGirati: number;
  attesoAdOggi: number;
  // True se il cliente è indietro sul ritmo mensile (reel girati < attesi a
  // oggi). NB: la soppressione per "riprese già fissate in agenda"
  // (ripreseFissate) resta a carico del chiamante — sul Videomaker cambia anche
  // il bordo card, sul Dashboard è un semplice AND nel badge.
  inRitardoRiprese: boolean;
}

// Valuta lo stato riprese di un cliente in modo identico ovunque.
export const valutaRiprese = (
  contenutiCliente: Contenuto[],
  reelMensili: number,
  meseCorrente: number,
  annoCorrente: number,
  giornoCorrente: number,
  giorniMese: number,
): StatoRiprese => {
  const reelGiaGirati = reelGiaGiratiCliente(contenutiCliente, meseCorrente, annoCorrente);
  const attesoAdOggi = Math.ceil((giornoCorrente / giorniMese) * reelMensili);
  const inRitardoRiprese = reelMensili > 0 && reelGiaGirati < attesoAdOggi;
  return { reelGiaGirati, attesoAdOggi, inRitardoRiprese };
};

// Un appuntamento appena passato vale ancora come "riprese coperte": tra il
// giorno delle riprese e il momento in cui il videomaker marca i contenuti come
// registrati passano dei giorni, e nel frattempo l'alert "Occorrono subito delle
// riprese" sarebbe un falso allarme. Dashboard e Videomaker leggono quindi gli
// appuntamenti a partire da qualche giorno indietro, non da oggi.
export const GIORNI_RIPRESE_RECENTI = 7;

// Endpoint da usare per la soppressione dell'alert riprese (NON per la pagina
// Appuntamenti, che tiene separate le viste "in programma" e "passati").
export const URL_APPUNTAMENTI_RIPRESE =
  `/api/videomaker/appuntamenti?daGiorni=${GIORNI_RIPRESE_RECENTI}`;

// Normalizza titoli/nomi per il matching appuntamento ↔ cliente:
// minuscolo, senza accenti, senza emoji/punteggiatura, spazi compressi.
export const normalizza = (s: string): string =>
  (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// Parole troppo generiche per identificare un cliente da sole.
const STOP_MATCH = new Set([
  'studio', 'legal', 'estates', 'official', 'group', 'agency', 'milano',
  'roma', 'italia', 'associazione', 'reels', 'riprese', 'video', 'shooting',
]);

const parole = (s: string): string[] =>
  normalizza(s).split(' ').filter((w) => w.length >= 4 && !STOP_MATCH.has(w));

// Distanza di Damerau-Levenshtein (edit distance con trasposizioni): serve a
// tollerare i refusi ricorrenti nei titoli scritti a mano dal videomaker sul
// calendario iCloud (es. "Arripol"/"Arrippol" per "Arippol").
const distanzaRefuso = (a: string, b: string): number => {
  const m = a.length, n = b.length;
  const d = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) d[i][0] = i;
  for (let j = 0; j <= n; j++) d[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[m][n];
};

// Due parole distintive combaciano se identiche o entro pochi refusi. La
// tolleranza cresce con la lunghezza: parole corte = zero margine, per non
// incrociare per sbaglio clienti con nomi simili.
const paroleCombaciano = (a: string, b: string): boolean => {
  if (a === b) return true;
  const L = Math.max(a.length, b.length);
  const tol = L >= 7 ? 2 : L >= 5 ? 1 : 0;
  return tol > 0 && distanzaRefuso(a, b) <= tol;
};

// Alias espliciti: diciture che i videomaker usano in agenda al posto del nome
// del cliente (es. il nome del titolare). Chiave = nome cliente normalizzato,
// valori = diciture equivalenti; ogni alias viene confrontato col titolo con la
// stessa logica (contenimento + parole distintive con tolleranza refusi).
const ALIAS_CLIENTI: Record<string, string[]> = {
  'la corte del fornaio': ['luca fornaio', 'luca pane'],
};

// Un appuntamento riguarda un cliente se i nomi normalizzati si contengono
// oppure condividono una parola distintiva (>=4 lettere, non generica),
// tollerando i refusi di battitura nei titoli del calendario.
export const appuntamentoRiguardaCliente = (nomeCliente: string, titoloApp: string): boolean => {
  const nc = normalizza(nomeCliente);
  const ta = normalizza(titoloApp);
  if (!nc || !ta) return false;
  if (nc.includes(ta) || ta.includes(nc)) return true;
  const paroleCliente = parole(nomeCliente);
  if (parole(titoloApp).some((w) => paroleCliente.some((pc) => paroleCombaciano(pc, w)))) return true;
  return (ALIAS_CLIENTI[nc] || []).some((alias) => appuntamentoRiguardaCliente(alias, titoloApp));
};

// Fra più clienti che combaciano con lo stesso titolo («Imperfetti 📹» sta sia con «Gli Imperfetti» sia con
// «Bakery Gli Imperfetti») vince quello con la quota più alta delle sue parole ritrovate nel titolo, a parità quello
// con più parole in comune: stessa regola di clientePiuProbabile nel backend (utils/clientCalendarFilter.ts).
// Prima un appuntamento valeva per tutti e due i clienti (12/10/2026).
export const clienteDellAppuntamento = (nomiClienti: string[], titoloApp: string): string | null => {
  const paroleTitolo = parole(titoloApp);
  let migliore: string | null = null;
  let migliorQuota = -1;
  let migliorTrovate = -1;
  for (const nome of nomiClienti) {
    if (!appuntamentoRiguardaCliente(nome, titoloApp)) continue;
    const pc = parole(nome);
    const trovate = pc.filter((w) => paroleTitolo.some((t) => paroleCombaciano(w, t))).length;
    const quota = pc.length > 0 ? trovate / pc.length : 0;
    if (quota > migliorQuota || (quota === migliorQuota && trovate > migliorTrovate)) {
      migliore = nome;
      migliorQuota = quota;
      migliorTrovate = trovate;
    }
  }
  return migliore;
};

// L'appuntamento è di questo cliente: lo riguarda e, fra i clienti attivi, è il più probabile.
export const appuntamentoDelCliente = (nomeCliente: string, titoloApp: string, nomiClientiAttivi: string[]): boolean =>
  appuntamentoRiguardaCliente(nomeCliente, titoloApp) &&
  (nomiClientiAttivi.length < 2 || clienteDellAppuntamento(nomiClientiAttivi, titoloApp) === nomeCliente);
